import { Link } from "react-router";

import { ExternalLinkIcon, ImageOffIcon, XIcon } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Button } from "@/components/ui/button";

export interface EntityRefDisplay {
  name: string;
  subtitle?: string;
  imageUrl?: string;
  /** In-app link to the referenced record, e.g. its edit page. */
  href?: string;
}

interface EntityRefPickerProps<T> {
  /** What the field points at now; `null` renders an empty state. */
  current: EntityRefDisplay | null;
  emptyLabel: string;
  onSearch: (query: string) => Promise<T[]>;
  accessorFn: (option: T) => { id: string; name: string; iconUrl?: string };
  onPick: (option: T) => void;
  /** Offer a clear button only where "points at nothing" is a valid state. */
  onClear?: () => void;
}

/**
 * A single-reference field: the record it points at (name, thumbnail, link) plus a search to
 * re-point it. The multi-value twin is `ShowGenresField`; this is its one-value shape, shared by
 * the screening movie/venue and the person → artist link.
 */
export function EntityRefPicker<T>({
  current,
  emptyLabel,
  onSearch,
  accessorFn,
  onPick,
  onClear,
}: EntityRefPickerProps<T>) {
  return (
    <div className="flex w-full flex-col gap-2 text-foreground">
      <div className="flex max-w-[500px] items-center gap-3 rounded-md border p-2">
        <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded bg-muted">
          {current?.imageUrl ? (
            <img
              src={current.imageUrl}
              alt=""
              className="size-full object-cover"
            />
          ) : (
            <ImageOffIcon className="size-5 text-muted-foreground" />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium">
            {current?.name ?? emptyLabel}
          </span>
          {current?.subtitle && (
            <span className="truncate text-xs text-muted-foreground">
              {current.subtitle}
            </span>
          )}
        </div>
        {current?.href && (
          <Button asChild variant="ghost" size="icon" title="Open">
            <Link to={current.href}>
              <ExternalLinkIcon className="size-4" />
            </Link>
          </Button>
        )}
        {current && onClear && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            title="Clear"
            aria-label="Clear"
            onClick={onClear}
          >
            <XIcon className="size-4" />
          </Button>
        )}
      </div>
      <RemoteSelect<T>
        className="w-[500px]"
        triggerType="change"
        onValueChanged={(option) => {
          if (option) onPick(option);
        }}
        accessorFn={accessorFn}
        onSearch={async (query, onStatusChanged) => {
          if (!query) return [];
          onStatusChanged("loading");
          const results = await onSearch(query);
          onStatusChanged("success");
          return results;
        }}
      />
    </div>
  );
}
